import { useEffect, useState } from "react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { listCancellationReasons } from "@/lib/orderApi";

/**
 * Cancel a table's order, confirmed by the staff member's own PIN.
 *
 * Anyone with table cancellation permissions can cancel an order. Their PIN is
 * re-verified server-side, and the cancellation runs under their session.
 * No manager PIN or manager ID is required.
 */
export function CaptainCancelDialog({ orderNumber, tableName, onConfirm, onClose }) {
  const { user, token } = usePOS();
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let live = true;
    listCancellationReasons(token)
      .then(rows => {
        if (!live) return;
        setReasons(rows.filter(r => r.applies_to !== "item"));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [token]);

  const submit = async e => {
    e.preventDefault();
    setError(null);
    if (!reasonId && !note.trim()) {
      return setError("Pick a reason or write what happened.");
    }
    setWorking(true);
    try {
      // The captain's own credential, verified by the server. The token this
      // returns is discarded — the point is the PIN check, not a new session.
      if (!pin) {
        setWorking(false);
        return setError("Confirm with your PIN.");
      }
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const check = await fetch(`${baseUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee_id: user?.employee_id, pin }),
      });
      if (!check.ok) {
        setWorking(false);
        return setError("That is not your PIN.");
      }

      await onConfirm({
        reasonCode: reasonId ? Number(reasonId) : null,
        reason: note.trim() || null,
        authorizedBy: null,
      });
    } catch (err) {
      setWorking(false);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || err?.message || "Could not cancel");
      return;
    }
    setWorking(false);
  };

  const field = "w-full h-12 px-3 rounded-lg border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary";
  const label = "block text-meta font-bold text-muted-foreground mb-tight uppercase";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-label="Cancel order"
      data-testid="captain-cancel-dialog"
    >
      <form className="bg-card w-full max-w-sm rounded-2xl shadow-xl border overflow-hidden" onSubmit={submit}>
        <div className="p-5 border-b">
          <h3 className="text-title font-bold text-foreground" data-testid="cancel-order-title">
            Cancel {orderNumber ? `Order #${orderNumber}` : `${tableName}'s order`}
          </h3>
          <p className="text-meta text-muted-foreground mt-1">
            Every line goes and the table is freed. Lines already with the kitchen get a VOID slip.
          </p>
        </div>

        <div className="p-5 space-y-base">
          <div>
            <label className={label}>Reason</label>
            <select className={field} value={reasonId} onChange={e => setReasonId(e.target.value)} data-testid="cancel-reason">
              <option value="">Pick a reason…</option>
              {reasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Note (optional)</label>
            <input className={field} value={note} onChange={e => setNote(e.target.value)} placeholder="What happened?" data-testid="cancel-note" />
          </div>

          <div>
            <label className={label}>Confirm with your PIN</label>
            <input
              className={field} type="password" inputMode="numeric" autoComplete="off"
              value={pin} onChange={e => setPin(e.target.value)}
              data-testid="cancel-own-pin"
            />
          </div>

          {error && <p className="text-meta font-semibold text-destructive">{error}</p>}
        </div>

        <div className="grid grid-cols-2 border-t divide-x">
          <button type="button" className="h-12 text-body font-bold text-muted-foreground hover:bg-muted/40" onClick={onClose} disabled={working}>
            Back
          </button>
          <button type="submit" className="h-12 text-body font-bold text-destructive hover:bg-destructive/10 disabled:opacity-50" disabled={working} data-testid="cancel-confirm">
            {working ? "Cancelling…" : "Cancel Order"}
          </button>
        </div>
      </form>
    </div>
  );
}
